import React, { Component } from 'react';
import { styled } from '@compiled/react';
import Avatar from '@atlaskit/avatar';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import Picker from '@atlassian/jira-common-components-picker/src/base/index.tsx';
import type { SelectOption } from '@atlassian/jira-common-components-picker/src/index.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import messages from './messages.tsx';
import type { Priority } from './model/index.tsx';
import { requestProjectPriorityList } from './services/index.tsx';

export type Props = {
	isRequired: boolean;
	isDisabled: boolean;
	// Whether the Picker can be cleared using a cross icon next to the selected value
	isClearableWithCrossIcon?: boolean;
	inputId?: string;
	noPortal: boolean;
	label: string | undefined;
	// @deprecated baseUrl is always '', remove it from your packages: go/baseurl-cleanup-jfe
	baseUrl?: string;
	projectIdOrKey: string;
	value: string | undefined;
	/** Focus the control when it is mounted */
	autoFocus?: boolean;
	onChange: (value: string) => void;
	intl: IntlShape;
};

type State = {
	isLoading: boolean;
	options: Priority[];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formatOptionLabel = (option: any) => (
	<OptionWrapper>
		{option.iconUrl ? (
			<IconWrapper>
				<Avatar src={option.iconUrl} size="xsmall" borderColor="transparent" />
			</IconWrapper>
		) : null}
		{option.label}
	</OptionWrapper>
);

const LoadingMessage = () => (
	<LoadingWrapper>
		<Spinner size="medium" />
	</LoadingWrapper>
);

// eslint-disable-next-line jira/react/no-class-components
export class PriorityPicker extends Component<Props, State> {
	static defaultProps = {
		noPortal: false,
		autoFocus: false,
	};

	state = {
		isLoading: true,
		options: [],
	};

	onSelected = (selectedOption: SelectOption) =>
		// selectedOption.value is only available when use picks concrete value
		// this callback is also called when user clears a value and in such a case selectedOption is null
		this.props.onChange(selectedOption ? selectedOption.value : selectedOption);

	renderOptions = () => {
		const promise = requestProjectPriorityList(this.props.projectIdOrKey);

		promise.then((data) => this.setState({ options: data }));

		return promise;
	};

	render() {
		const {
			inputId,
			label,
			isRequired,
			isDisabled,
			intl: { formatMessage },
			value,
			isClearableWithCrossIcon,
			noPortal,
		} = this.props;
		const { isLoading, options } = this.state;

		// If we want to deselect value we need to set it to null
		// See more on this thread:
		// https://stackoverflow.com/questions/50412843/how-to-programmatically-clear-reset-react-select-v2/50417171
		const currentSelection: Priority | null =
			// @ts-expect-error - TS2339 - Property 'value' does not exist on type 'never'.
			options.find((option) => option.value === value) || null;

		// if field is disabled we should pass isSearchable=true, because in current version of react-select there is a bug:
		// when isDisabled=true and isSearchable=false user will be able to focus field (using tab key)
		// https://github.com/JedWatson/react-select/pull/3163
		const isSearchable = isDisabled;

		return (
			<Picker
				inputId={inputId}
				isClearableWithCrossIcon={isClearableWithCrossIcon}
				noPortal={noPortal}
				label={label}
				autoFocus={!!this.props.autoFocus}
				placeholder={formatMessage(messages.placeholder)}
				isRequired={isRequired}
				isDisabled={isDisabled}
				isLoading={isLoading}
				value={currentSelection}
				errorMessage={formatMessage(messages.error)}
				options={this.renderOptions}
				// @ts-expect-error - Type '(selectedOption: SelectOption) => void' is not assignable to type '((arg1: Priority | undefined) => void) & ((arg1: Priority | undefined, arg2: UIAnalyticsEvent | undefined) => void) & ((arg1: Priority | undefined, arg2: UIAnalyticsEvent | undefined) => void)'.
				onSelected={this.onSelected}
				formatOptionLabel={formatOptionLabel}
				components={{ LoadingMessage }}
				isSearchable={isSearchable}
				// https://hello.atlassian.net/wiki/spaces/~989411314/pages/2861097485/Investigation+Notes+for+atlaskit+select+react-beautiful-dnd
				hasDraggableParentComponent
			/>
		);
	}
}

export default injectIntl(PriorityPicker);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.span({
	marginRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minHeight: `${gridSize * 6}px`,
});
